import React from 'react';
import { motion } from 'framer-motion';

const ContactCard = ({ icon, label, val, link }) => (
  <a 
    href={link}
    className="group p-8 md:p-10 rounded-[2.5rem] glass-surface-v2 border border-theme/10 flex flex-col items-center text-center hover:border-bronze-500/30 transition-all duration-700"
  >
    <div className="w-12 h-12 rounded-2xl bg-bronze-500/10 flex items-center justify-center text-bronze-400 mb-6 group-hover:scale-110 transition-transform duration-500">
      <i className={`fas ${icon} text-xl`} />
    </div>
    <span className="text-[10px] font-mono uppercase tracking-ultra text-theme-muted mb-3 block">{label}</span>
    <span className="text-sm md:text-base font-serif italic text-theme">{val}</span>
  </a>
);

const ContactUs = () => {
  return (
    <div className="bg-theme text-theme pt-32 overflow-hidden">
      
      {/* ── HERO SECTION ── */}
      <section className="px-6 md:px-20 max-w-7xl mx-auto mb-24 md:mb-40">
        <motion.div
           initial={{ opacity: 0, y: 30 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ duration: 1 }}
        >
          <span className="text-[10px] font-mono uppercase tracking-ultra text-bronze-400 mb-6 block">Immediate Protocol</span>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif font-light leading-[1.1] mb-12">
            Get In <br /> 
            <span className="italic text-bronze-gradient">Touch.</span>
          </h1>
        </motion.div>
      </section>

      {/* ── QUICK CHANNELS ── */}
      <section className="px-6 md:px-20 max-w-7xl mx-auto mb-24 md:mb-40 grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
        <ContactCard 
          icon="fa-phone"
          label="Direct Line"
          val="+91 8984-684-689"
          link="tel:+918984684689"
        />
        <ContactCard 
          icon="fa-envelope"
          label="Inquiry Mail"
          val="info@iotiq.co.in"
          link="mailto:info@iotiq.co.in"
        />
        <ContactCard 
          icon="fa-whatsapp"
          label="WhatsApp Quick"
          val="Chat with Expert"
          link="https://wa.me/918984684689"
        />
      </section>

      {/* ── MAIN GRID ── */}
      <section className="px-6 md:px-20 max-w-7xl mx-auto mb-32 md:mb-60 grid lg:grid-cols-2 gap-20 md:gap-32">
        
        {/* Form Container */}
        <div>
           <SectionHeader 
             eyebrow="Formal Engagement"
             title="Project Inquiry."
             sub="Tell us about your residential or industrial requirements. 24-hour response protocol guaranteed."
           />
           
           <form className="space-y-6 mt-12">
              <div className="grid md:grid-cols-2 gap-6">
                 <div>
                    <label className="text-[9px] uppercase tracking-ultra text-theme-muted mb-3 block">Full Name</label>
                    <input type="text" placeholder="John Doe" className="w-full bg-theme/30 border border-theme/20 rounded-2xl p-4 px-6 text-sm focus:outline-none focus:border-bronze-500/50 transition-colors" />
                 </div>
                 <div>
                    <label className="text-[9px] uppercase tracking-ultra text-theme-muted mb-3 block">Phone Number</label>
                    <input type="tel" placeholder="+91 0000 000 000" className="w-full bg-theme/30 border border-theme/20 rounded-2xl p-4 px-6 text-sm focus:outline-none focus:border-bronze-500/50 transition-colors" />
                 </div>
              </div>
              <div>
                 <label className="text-[9px] uppercase tracking-ultra text-theme-muted mb-3 block">Email Address</label>
                 <input type="email" placeholder="john@example.com" className="w-full bg-theme/30 border border-theme/20 rounded-2xl p-4 px-6 text-sm focus:outline-none focus:border-bronze-500/50 transition-colors" />
              </div>
              <div>
                 <label className="text-[9px] uppercase tracking-ultra text-theme-muted mb-3 block">Interest Area</label>
                 <select className="w-full bg-theme/30 border border-theme/20 rounded-2xl p-4 px-6 text-sm focus:outline-none focus:border-bronze-500/50 transition-colors appearance-none">
                    <option>Home Automation (B2C)</option>
                    <option>Industrial Automation (B2B)</option>
                    <option>Experience Center Visit</option>
                    <option>Retail Partnership</option>
                 </select>
              </div>
              <div>
                 <label className="text-[9px] uppercase tracking-ultra text-theme-muted mb-3 block">Message</label>
                 <textarea rows="4" placeholder="How can we help?" className="w-full bg-theme/30 border border-theme/20 rounded-2xl p-4 px-6 text-sm focus:outline-none focus:border-bronze-500/50 transition-colors resize-none"></textarea>
              </div>
              <button className="w-full py-5 bg-bronze-gradient text-obsidian rounded-full text-[10px] uppercase tracking-ultra font-bold shadow-xl hover:shadow-gold-glow-sm transition-all duration-500">
                 Dispatch Inquiry
              </button>
           </form>
        </div>

        {/* Info & Map */}
        <div className="flex flex-col gap-12">
           <div className="rounded-[3rem] overflow-hidden glass-surface-v2 border border-theme/10 aspect-[4/3] relative group shadow-2xl">
              <img src="https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&q=80&w=1500" alt="Experience Center Showroom" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[3s]" />
              <div className="absolute inset-0 bg-gradient-to-t from-theme via-transparent to-transparent opacity-60" />
              <div className="absolute bottom-10 left-10">
                 <span className="text-[10px] uppercase tracking-ultra text-bronze-400 mb-3 block font-bold">Physical Vector</span>
                 <h3 className="text-2xl font-serif italic text-white">The Experience Center</h3>
              </div>
           </div>
           
           <div className="space-y-8">
              <div>
                 <h4 className="text-xs uppercase tracking-ultra text-bronze-400 mb-4 font-bold leading-loose">Hyderabad HQ</h4>
                 <p className="text-theme-secondary text-sm md:text-base font-serif italic leading-relaxed">
                   ACS/IOTIQ Facility, <br />
                   Balanagar Industrial Estate, <br />
                   Hyderabad, TS 500037, India.
                 </p>
              </div>
              <div>
                 <h4 className="text-xs uppercase tracking-ultra text-bronze-400 mb-4 font-bold leading-loose">Operating Protocol</h4>
                 <p className="text-theme-secondary text-sm md:text-base font-serif italic leading-relaxed">
                   Monday – Saturday <br />
                   09:00 – 19:30 IST
                 </p>
              </div>
           </div>
        </div>

      </section>

      {/* Decorative End */}
      <div className="h-40 w-px bg-gradient-to-b from-bronze-500/20 to-transparent mx-auto" />

    </div>
  );
};

const SectionHeader = ({ eyebrow, title, sub }) => (
  <div className="mb-0">
    <span className="text-[10px] font-mono uppercase tracking-ultra text-bronze-400 mb-4 block font-bold leading-loose">{eyebrow}</span>
    <h2 className="text-3xl md:text-4xl font-serif font-light mb-6 text-theme">{title}</h2>
    <p className="text-xs md:text-sm text-theme-secondary max-w-lg leading-relaxed font-light">{sub}</p>
  </div>
);

export default ContactUs;
